import { useIntl } from 'react-intl'
import {
  csx,
  experimental_DatePickerCalendar as DatePickerCalendar,
  experimental_DatePickerField as DatePickerField,
  type experimental_useDatePickerState as useDatePickerState,
  FilterFooter,
  FilterListbox,
  FilterOptionRadio,
  FilterPopover,
  Flex,
  Stack,
} from '@vtex/admin-ui'
import { WithFeatureFlag } from '@/components/feature-flag/with-feature-flag'
import { BaseFilter } from '@/components/filters/BaseFilter/BaseFilter'

import { useFilterCreatedAtState } from './hooks/useFilterCreatedAtState'

import * as styles from './FilterCreatedAt.styles'

type DatePickerState = ReturnType<typeof useDatePickerState>

interface DateRangeProps {
  startDate: DatePickerState
  endDate: DatePickerState
}

function DateRange(props: DateRangeProps) {
  const { startDate, endDate } = props

  const { formatMessage } = useIntl()

  return (
    <Stack
      space="$space-3"
      className={csx({ ...styles.stack, ...styles.fitWidth })}
    >
      <Flex className={csx(styles.flex)}>
        <DatePickerField
          className={csx(styles.dateField)}
          label={formatMessage({
            id: 'admin/admin.catalog.created-at-filter-start-date-label',
            defaultMessage: 'Start date',
          })}
          state={startDate}
        />
        <DatePickerCalendar
          className={csx(styles.calendar)}
          state={startDate}
        />
      </Flex>
      <Flex className={csx(styles.flex)}>
        <DatePickerField
          className={csx(styles.dateField)}
          label={formatMessage({
            id: 'admin/admin.catalog.created-at-filter-end-date-label',
            defaultMessage: 'End date',
          })}
          state={endDate}
        />
        <DatePickerCalendar className={csx(styles.calendar)} state={endDate} />
      </Flex>
    </Stack>
  )
}

interface FilterCreatedAtProps {
  state: ReturnType<typeof useFilterCreatedAtState>
}

function FilterCreatedAtPopover(props: FilterCreatedAtProps) {
  const {
    state: {
      pickerState: { startDate, endDate },
      ...filterState
    },
  } = props

  const { formatMessage } = useIntl()

  return (
    <FilterPopover state={filterState} className={csx(styles.fitWidth)}>
      <FilterListbox>
        <FilterOptionRadio
          id="today"
          value="today"
          label={formatMessage({
            id: 'admin/admin.catalog.created-at-filter-today-option',
            defaultMessage: 'Today',
          })}
        />
        <FilterOptionRadio
          id="last-7-days"
          value="last-7-days"
          label={formatMessage({
            id: 'admin/admin.catalog.created-at-filter-last-seven-days-option',
            defaultMessage: 'Last 7 days',
          })}
        />
        <WithFeatureFlag featureFlagName="created_at_filter__last_30_days">
          <FilterOptionRadio
            id="last-30-days"
            value="last-30-days"
            label={formatMessage({
              id: 'admin/admin.catalog.created-at-filter-last-thirty-days-option',
              defaultMessage: 'Last 30 days',
            })}
          />
        </WithFeatureFlag>
        <WithFeatureFlag featureFlagName="created_at_filter__custom">
          <FilterOptionRadio
            id="custom"
            value="custom"
            label={formatMessage({
              id: 'admin/admin.catalog.created-at-filter-custom-option',
              defaultMessage: 'Custom',
            })}
          />
        </WithFeatureFlag>
      </FilterListbox>
      {filterState.combobox.selectedItem?.id === 'custom' && (
        <DateRange startDate={startDate} endDate={endDate} />
      )}
      <FilterFooter />
    </FilterPopover>
  )
}

export type SerializedData = {
  startDate: string
  endDate: string
  appliedItem: 'today' | 'last-7-days' | 'last-30-days' | 'custom' | ''
}

export function FilterCreatedAt(props: { name: string }) {
  const { formatMessage } = useIntl()

  return (
    <BaseFilter
      filterName={props.name}
      useFilterState={useFilterCreatedAtState}
      FilterPopoverComponent={FilterCreatedAtPopover}
      disclosureMessage={formatMessage({
        id: 'admin/admin.catalog.created-at-filter',
        defaultMessage: 'Created date',
      })}
      toSerializableData={(data) =>
        ({
          startDate: data.appliedDates.startDate ?? '',
          endDate: data.appliedDates.endDate ?? '',
          appliedItem: data.appliedItem?.value ?? '',
        }) as SerializedData
      }
    />
  )
}
